import type { SwapTokenInfo } from '@/hooks/use-tokens'
import type { Candle } from '@/providers/liquidity-chart/candle-utils'

import dict from '../dict'
import { api } from './'

const ONE_MINUTE_IN_SEC = 60
const ONE_HOUR_IN_SEC = 60 * ONE_MINUTE_IN_SEC
export const ONE_DAY_IN_SEC = 24 * ONE_HOUR_IN_SEC

export type Kline = {
  t: string
  l: number
  h: number
  o: number
  c: number
  v: number
}
type TradingViewTickerAPIResponse = {
  pair: `${string}/${string}`
  klines: Array<{
    t: string
    l: number
    h: number
    o: number
    c: number
    v: number
  }>
}

export type TradingViewTickerTimeFrame =
  | 'one_minute'
  | 'five_minutes'
  | 'fifteen_minutes'
  | 'thirty_minutes'
  | 'one_hour'
  | 'four_hours'
  | 'twenty_four_hour'
  | 'one_week'
  | 'one_month'

export const tickerTimeFrameToSeconds: Record<TradingViewTickerTimeFrame, number> = Object.assign(
  dict(ONE_DAY_IN_SEC),
  {
    one_minute: ONE_MINUTE_IN_SEC,
    five_minutes: 5 * ONE_MINUTE_IN_SEC,
    fifteen_minutes: 15 * ONE_MINUTE_IN_SEC,
    thirty_minutes: 30 * ONE_MINUTE_IN_SEC,
    one_hour: 1 * ONE_HOUR_IN_SEC,
    four_hours: 4 * ONE_HOUR_IN_SEC,
    twenty_four_hour: 24 * ONE_HOUR_IN_SEC,
    one_week: 1 * 7 * ONE_DAY_IN_SEC,
    one_month: 1 * 30 * ONE_DAY_IN_SEC,
  },
)

export const secondsToTickerTimeFrame: Record<number, TradingViewTickerTimeFrame> = Object.assign(
  dict('twenty_four_hour'),
  Object.fromEntries(
    Object.entries(tickerTimeFrameToSeconds).map(([key, value]) => [
      value,
      key as TradingViewTickerTimeFrame,
    ]),
  ),
)

export const getTickers = async (
  tokenA: SwapTokenInfo,
  tokenB: SwapTokenInfo,
  timeFrame: TradingViewTickerTimeFrame = 'twenty_four_hour',
  startTime?: number,
) => {
  const { data } = await api.get<TradingViewTickerAPIResponse>('/tickers/trading_view', {
    params: {
      token0: tokenA.symbol,
      token1: tokenB.symbol,
      time_frame: timeFrame,
      start_time: startTime,
    },
  })

  const candles = data.klines
    .map<Candle>((kline) => ({
      open: kline.o,
      close: kline.c,
      low: kline.l,
      high: kline.h,
      span: tickerTimeFrameToSeconds[timeFrame],
      startTime: new Date(kline.t),
      volume: kline.v,
    }))
    .sort((a, b) => a.startTime.getTime() - b.startTime.getTime())

  const [token0] = data.pair.split('/')
  const isTokenA0 = tokenA.symbol.toLowerCase() === token0
  return {
    tokenA: isTokenA0 ? tokenA : tokenB,
    tokenB: isTokenA0 ? tokenB : tokenA,
    candles,
  }
}
